import React, { memo } from 'react'
import tw from 'twin.macro'
import { Chip } from 'src/components/shared/Chip'
import { partnerInvoiceStatusLabels } from 'src/constants/invoice/constants'
import { PartnerInvoiceStatus } from 'src/constants/invoice/enums'
import type { PartnerInvoice } from 'src/interfaces/invoice'
import { isPartnerInvoiceFundingFailed } from 'src/utils/invoiceStatus'

interface Props {
  invoice: PartnerInvoice
}

const chipStyles: Record<PartnerInvoiceStatus, any> = {
  [PartnerInvoiceStatus.Created]: tw`bg-shades-100`,
  [PartnerInvoiceStatus.Pending]: tw`bg-shades-100`,
  [PartnerInvoiceStatus.Canceled]: tw`bg-shades-100`,
  [PartnerInvoiceStatus.Funded]: tw`bg-primary-100`,
  [PartnerInvoiceStatus.RepaymentPending]: tw`bg-primary-100`,
  [PartnerInvoiceStatus.Repaid]: tw`bg-success-200`,
}

// Text follows the tint the same way `ComposedInvoiceStatusCell` does: green on green, purple
// on purple, red for a failed funding, dark grey on the neutral chips.
const chipTextStyles: Record<PartnerInvoiceStatus, any> = {
  [PartnerInvoiceStatus.Created]: tw`text-shades-700`,
  [PartnerInvoiceStatus.Pending]: tw`text-shades-700`,
  [PartnerInvoiceStatus.Canceled]: tw`text-shades-700`,
  [PartnerInvoiceStatus.Funded]: tw`text-primary-700`,
  [PartnerInvoiceStatus.RepaymentPending]: tw`text-primary-700`,
  [PartnerInvoiceStatus.Repaid]: tw`text-success-500`,
}

const PartnerInvoiceStatusCellComponent = ({ invoice }: Props) => {
  // The raw backend status, not a derived one — this is the detail view, where an admin needs
  // the real value. A failed funding attempt is called out because the status alone
  // ('created', 'funded') does not reveal it.
  const label = isPartnerInvoiceFundingFailed(invoice)
    ? `${partnerInvoiceStatusLabels[invoice.status]} · funding failed`
    : partnerInvoiceStatusLabels[invoice.status]

  const style = isPartnerInvoiceFundingFailed(invoice)
    ? tw`bg-critical-50`
    : chipStyles[invoice.status]
  const textStyle = isPartnerInvoiceFundingFailed(invoice)
    ? tw`text-critical-500`
    : chipTextStyles[invoice.status]

  return (
    <Chip
      text={label}
      className={style}
      textClassName={textStyle}
      dataTestId={`partner-invoices-list-status-${invoice.id}`}
    />
  )
}

export const PartnerInvoiceStatusCell = memo(PartnerInvoiceStatusCellComponent)
